import { useState, useEffect, useCallback, KeyboardEvent } from 'react';
import { Plus, Trash2, GripVertical, AlertTriangle, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LayoutType, SceneData } from '@/types/course.types';

interface SceneDataEditorProps {
  value?: SceneData;
  onChange: (data: SceneData) => void;
  readOnly?: boolean;
}

const LAYOUT_OPTIONS: { value: LayoutType; label: string; icon: string; description: string }[] = [
  { value: 'title', label: 'Title', icon: 'T', description: 'Heading + subtitle' },
  { value: 'content', label: 'Content', icon: '\u00B6', description: 'Body paragraph' },
  { value: 'bullet', label: 'Bullets', icon: '\u2022', description: 'Bullet list' },
  { value: 'code', label: 'Code', icon: '<>', description: 'Code block' },
  { value: 'comparison', label: 'Compare', icon: '\u21C4', description: 'Side-by-side' },
  { value: 'image_text', label: 'Image', icon: '\u25A3', description: 'Image + text' },
  { value: 'summary', label: 'Summary', icon: '\u2713', description: 'Takeaways' },
  { value: 'qa', label: 'Q&A', icon: '?', description: 'Question/answer' },
  { value: 'timeline', label: 'Timeline', icon: '\u2192', description: 'Step nodes' },
  { value: 'stats', label: 'Stats', icon: '#', description: 'Key numbers' },
  { value: 'quote', label: 'Quote', icon: '\u201C', description: 'Quotation' },
  { value: 'process', label: 'Process', icon: '\u21BB', description: 'Flow steps' },
];

/** Validation rules per layout type — which fields MUST be non-empty */
const REQUIRED_FIELDS: Record<LayoutType, string[]> = {
  title: ['heading', 'subheading'],
  content: ['heading', 'body_text'],
  bullet: ['heading', 'bullet_points'],
  code: ['heading', 'code_snippet'],
  comparison: ['heading', 'comparison'],
  image_text: ['heading', 'body_text'],
  summary: ['heading', 'key_takeaways'],
  qa: ['heading', 'question', 'answer'],
  timeline: ['heading', 'steps'],
  stats: ['heading', 'stats'],
  quote: ['quote_text'],
  process: ['heading', 'steps'],
};

function getValidationWarnings(data: SceneData): string[] {
  const required = REQUIRED_FIELDS[data.layout_type] || ['heading'];
  const warnings: string[] = [];

  for (const field of required) {
    const val = (data as any)[field];
    if (field === 'bullet_points' || field === 'key_takeaways') {
      if (!val || !Array.isArray(val) || val.length === 0 || val.every((v: string) => !v.trim())) {
        warnings.push(`${field.replace(/_/g, ' ')} is empty — slide will render blank`);
      }
    } else if (field === 'steps') {
      if (!val || !Array.isArray(val) || val.length === 0) {
        warnings.push('steps is empty — slide will render blank');
      }
    } else if (field === 'stats') {
      if (!val || !Array.isArray(val) || val.length === 0) {
        warnings.push('stats is empty — slide will render blank');
      }
    } else if (field === 'code_snippet') {
      if (!val || !val.code?.trim()) {
        warnings.push('code snippet is empty — slide will render blank');
      }
    } else if (field === 'comparison') {
      if (!val || (!val.left?.title && !val.right?.title)) {
        warnings.push('comparison columns are empty — slide will render blank');
      }
    } else if (!val || (typeof val === 'string' && !val.trim())) {
      warnings.push(`${field.replace(/_/g, ' ')} is required for ${data.layout_type} layout`);
    }
  }

  return warnings;
}

const DEFAULT_SCENE_DATA: SceneData = {
  layout_type: 'content',
  heading: '',
};

export default function SceneDataEditor({ value, onChange, readOnly = false }: SceneDataEditorProps) {
  const [data, setData] = useState<SceneData>(value || DEFAULT_SCENE_DATA);

  useEffect(() => {
    if (value) setData(value);
  }, [value]);

  const update = useCallback((partial: Partial<SceneData>) => {
    setData((prev) => {
      const next = { ...prev, ...partial };
      onChange(next);
      return next;
    });
  }, [onChange]);

  const layout = data.layout_type;
  const warnings = getValidationWarnings(data);

  return (
    <div className="space-y-3">
      {/* Layout Type — Visual Card Picker */}
      <div>
        <label className="block text-xs font-bold text-gray-900 mb-1.5">Layout Type</label>
        <div className="grid grid-cols-4 gap-1.5">
          {LAYOUT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={readOnly}
              onClick={() => update({ layout_type: opt.value })}
              className={`flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-lg border-2 text-center transition-all ${
                layout === opt.value
                  ? 'border-brand-purple-500 bg-brand-purple-50 text-brand-purple-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              } ${readOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className={`text-sm font-bold leading-none ${layout === opt.value ? 'text-brand-purple-600' : 'text-gray-400'}`}>
                {opt.icon}
              </span>
              <span className="text-[10px] font-semibold leading-tight truncate w-full">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Validation warnings */}
      {warnings.length > 0 && !readOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Heading (all layouts) */}
      <Field label="Heading" value={data.heading} onChange={(v) => update({ heading: v })} readOnly={readOnly} />

      {/* Enrichment fields */}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Icon (emoji)" value={data.icon || ''} onChange={(v) => update({ icon: v || undefined })} readOnly={readOnly} placeholder="e.g. &#x1F4A1;" />
        <Field label="Tag Label" value={data.tag_label || ''} onChange={(v) => update({ tag_label: v || undefined })} readOnly={readOnly} placeholder="e.g. KEY CONCEPT" />
      </div>

      {/* Layout-specific fields */}
      {layout === 'title' && (
        <Field label="Subheading" value={data.subheading || ''} onChange={(v) => update({ subheading: v || undefined })} readOnly={readOnly} />
      )}

      {(layout === 'content' || layout === 'image_text') && (
        <TextArea label="Body Text" value={data.body_text || ''} onChange={(v) => update({ body_text: v || undefined })} readOnly={readOnly} rows={3} />
      )}

      {layout === 'bullet' && (
        <SortableStringList label="Bullet Points" values={data.bullet_points || []} onChange={(v) => update({ bullet_points: v })} readOnly={readOnly} placeholder="Add a bullet point" />
      )}

      {layout === 'code' && (
        <>
          <Field label="Language" value={data.code_snippet?.language || ''} onChange={(v) => update({ code_snippet: { language: v, code: data.code_snippet?.code || '' } })} readOnly={readOnly} placeholder="e.g. javascript" />
          <TextArea label="Code" value={data.code_snippet?.code || ''} onChange={(v) => update({ code_snippet: { language: data.code_snippet?.language || '', code: v } })} readOnly={readOnly} rows={5} mono />
        </>
      )}

      {layout === 'comparison' && (
        <ComparisonEditor
          value={data.comparison || { left: { title: '', points: [] }, right: { title: '', points: [] } }}
          onChange={(v) => update({ comparison: v })}
          readOnly={readOnly}
        />
      )}

      {layout === 'summary' && (
        <SortableStringList label="Key Takeaways" values={data.key_takeaways || []} onChange={(v) => update({ key_takeaways: v })} readOnly={readOnly} placeholder="Add a takeaway" />
      )}

      {layout === 'qa' && (
        <>
          <TextArea label="Question" value={data.question || ''} onChange={(v) => update({ question: v || undefined })} readOnly={readOnly} rows={2} />
          <TextArea label="Answer" value={data.answer || ''} onChange={(v) => update({ answer: v || undefined })} readOnly={readOnly} rows={3} />
        </>
      )}

      {layout === 'timeline' && (
        <StepsEditor
          values={(data.steps || []).map((s) => (typeof s === 'string' ? { title: s } : s))}
          onChange={(v) => update({ steps: v })}
          readOnly={readOnly}
          showDescription={false}
        />
      )}

      {layout === 'process' && (
        <StepsEditor
          values={(data.steps || []).map((s) => (typeof s === 'string' ? { title: s } : s))}
          onChange={(v) => update({ steps: v })}
          readOnly={readOnly}
          showDescription
        />
      )}

      {layout === 'stats' && (
        <StatsEditor values={data.stats || []} onChange={(v) => update({ stats: v })} readOnly={readOnly} />
      )}

      {layout === 'quote' && (
        <>
          <TextArea label="Quote Text" value={data.quote_text || ''} onChange={(v) => update({ quote_text: v || undefined })} readOnly={readOnly} rows={3} />
          <Field label="Attribution" value={data.attribution || ''} onChange={(v) => update({ attribution: v || undefined })} readOnly={readOnly} placeholder="e.g. — Albert Einstein" />
        </>
      )}

      {layout === 'image_text' && (
        <SortableStringList label="Image URLs" values={data.image_urls || []} onChange={(v) => update({ image_urls: v })} readOnly={readOnly} placeholder="https://..." />
      )}

      {/* Highlight words (tag input) */}
      <TagInput label="Highlight Words" values={data.highlight_words || []} onChange={(v) => update({ highlight_words: v.length ? v : undefined })} readOnly={readOnly} placeholder="Type a word and press Enter" />

      {/* Callout text (optional for all) */}
      <Field label="Callout Text" value={data.callout_text || ''} onChange={(v) => update({ callout_text: v || undefined })} readOnly={readOnly} placeholder="Short emphasis phrase (8-15 words)" />
    </div>
  );
}

/* ─── Reusable sub-components ─── */

function Field({ label, value, onChange, readOnly, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; readOnly?: boolean; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, readOnly, rows = 3, mono }: {
  label: string; value: string; onChange: (v: string) => void; readOnly?: boolean; rows?: number; mono?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        rows={rows}
        className={`w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500 resize-none scrollbar-hide ${mono ? 'font-mono text-xs' : ''}`}
      />
    </div>
  );
}

/** Tag-style input for highlight_words */
function TagInput({ label, values, onChange, readOnly, placeholder }: {
  label: string; values: string[]; onChange: (v: string[]) => void; readOnly?: boolean; placeholder?: string;
}) {
  const [input, setInput] = useState('');

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
    if (e.key === 'Backspace' && !input && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const remove = (i: number) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1.5 p-1.5 border border-gray-300 rounded-lg min-h-[34px] bg-white focus-within:ring-2 focus-within:ring-brand-purple-500">
        {values.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-0.5 bg-brand-purple-100 text-brand-purple-700 rounded px-1.5 py-0.5 text-xs font-medium">
            {tag}
            {!readOnly && (
              <button type="button" onClick={() => remove(i)} className="hover:text-brand-purple-900">
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </span>
        ))}
        {!readOnly && (
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={addTag}
            placeholder={values.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[80px] text-sm outline-none border-none bg-transparent px-1 py-0.5"
          />
        )}
      </div>
      {!readOnly && <p className="text-[10px] text-gray-400 mt-0.5">Press Enter or comma to add</p>}
    </div>
  );
}

/* ─── Sortable string list with drag handles ─── */

function SortableStringItem({ id, value: val, index, onChange: onItemChange, onRemove, readOnly, placeholder }: {
  id: string; value: string; index: number; onChange: (v: string) => void; onRemove: () => void; readOnly?: boolean; placeholder?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-1.5 items-center">
      {!readOnly && (
        <button type="button" {...attributes} {...listeners} className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing">
          <GripVertical className="w-3.5 h-3.5" />
        </button>
      )}
      <input
        type="text"
        value={val}
        onChange={(e) => onItemChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
      />
      {!readOnly && (
        <button type="button" onClick={onRemove} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function SortableStringList({ label, values, onChange, readOnly, placeholder }: {
  label: string; values: string[]; onChange: (v: string[]) => void; readOnly?: boolean; placeholder?: string;
}) {
  // Stable IDs for sortable context
  const [ids, setIds] = useState<string[]>(() => values.map((_, i) => `item-${i}-${Date.now()}`));

  useEffect(() => {
    // Sync IDs when values length changes externally
    if (ids.length !== values.length) {
      setIds(values.map((_, i) => ids[i] || `item-${i}-${Date.now()}`));
    }
  }, [values.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const add = () => {
    onChange([...values, '']);
    setIds((prev) => [...prev, `item-${prev.length}-${Date.now()}`]);
  };

  const remove = (i: number) => {
    onChange(values.filter((_, idx) => idx !== i));
    setIds((prev) => prev.filter((_, idx) => idx !== i));
  };

  const updateItem = (i: number, v: string) => {
    const next = [...values];
    next[i] = v;
    onChange(next);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const old_index = ids.indexOf(String(active.id));
      const new_index = ids.indexOf(String(over.id));
      if (old_index !== -1 && new_index !== -1) {
        onChange(arrayMove(values, old_index, new_index));
        setIds(arrayMove(ids, old_index, new_index));
      }
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {values.map((item, i) => (
              <SortableStringItem
                key={ids[i]}
                id={ids[i]}
                value={item}
                index={i}
                onChange={(v) => updateItem(i, v)}
                onRemove={() => remove(i)}
                readOnly={readOnly}
                placeholder={placeholder}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {!readOnly && (
        <button type="button" onClick={add} className="mt-1.5 flex items-center gap-1 text-xs text-brand-purple-600 hover:text-brand-purple-700 font-medium">
          <Plus className="w-3 h-3" /> Add item
        </button>
      )}
    </div>
  );
}

function ComparisonEditor({ value, onChange, readOnly }: {
  value: { left: { title: string; points: string[] }; right: { title: string; points: string[] } };
  onChange: (v: { left: { title: string; points: string[] }; right: { title: string; points: string[] } }) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1.5">
        <Field label="Left Title" value={value.left.title} onChange={(v) => onChange({ ...value, left: { ...value.left, title: v } })} readOnly={readOnly} />
        <SortableStringList label="Left Points" values={value.left.points} onChange={(v) => onChange({ ...value, left: { ...value.left, points: v } })} readOnly={readOnly} placeholder="Point" />
      </div>
      <div className="space-y-1.5">
        <Field label="Right Title" value={value.right.title} onChange={(v) => onChange({ ...value, right: { ...value.right, title: v } })} readOnly={readOnly} />
        <SortableStringList label="Right Points" values={value.right.points} onChange={(v) => onChange({ ...value, right: { ...value.right, points: v } })} readOnly={readOnly} placeholder="Point" />
      </div>
    </div>
  );
}

function StepsEditor({ values, onChange, readOnly, showDescription }: {
  values: { title: string; description?: string }[];
  onChange: (v: { title: string; description?: string }[]) => void;
  readOnly?: boolean;
  showDescription?: boolean;
}) {
  const add = () => onChange([...values, { title: '' }]);
  const remove = (i: number) => onChange(values.filter((_, idx) => idx !== i));
  const updateStep = (i: number, field: 'title' | 'description', v: string) => {
    const next = [...values];
    next[i] = { ...next[i], [field]: v || undefined };
    onChange(next);
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">Steps</label>
      <div className="space-y-2">
        {values.map((step, i) => (
          <div key={i} className="flex gap-1.5 items-start">
            <span className="w-5 h-5 rounded-full bg-brand-purple-100 text-brand-purple-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-1">{i + 1}</span>
            <div className="flex-1 space-y-1">
              <input
                type="text"
                value={step.title}
                onChange={(e) => updateStep(i, 'title', e.target.value)}
                readOnly={readOnly}
                placeholder="Step title"
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
              />
              {showDescription && (
                <input
                  type="text"
                  value={step.description || ''}
                  onChange={(e) => updateStep(i, 'description', e.target.value)}
                  readOnly={readOnly}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
                />
              )}
            </div>
            {!readOnly && (
              <button type="button" onClick={() => remove(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-0.5">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!readOnly && (
        <button type="button" onClick={add} className="mt-1.5 flex items-center gap-1 text-xs text-brand-purple-600 hover:text-brand-purple-700 font-medium">
          <Plus className="w-3 h-3" /> Add step
        </button>
      )}
    </div>
  );
}

function StatsEditor({ values, onChange, readOnly }: {
  values: { value: string; label: string }[];
  onChange: (v: { value: string; label: string }[]) => void;
  readOnly?: boolean;
}) {
  const add = () => onChange([...values, { value: '', label: '' }]);
  const remove = (i: number) => onChange(values.filter((_, idx) => idx !== i));
  const updateStat = (i: number, field: 'value' | 'label', v: string) => {
    const next = [...values];
    next[i] = { ...next[i], [field]: v };
    onChange(next);
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">Stats (max 4)</label>
      <div className="space-y-1.5">
        {values.map((stat, i) => (
          <div key={i} className="flex gap-1.5">
            <input
              type="text"
              value={stat.value}
              onChange={(e) => updateStat(i, 'value', e.target.value)}
              readOnly={readOnly}
              placeholder="95%"
              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-center focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
            />
            <input
              type="text"
              value={stat.label}
              onChange={(e) => updateStat(i, 'label', e.target.value)}
              readOnly={readOnly}
              placeholder="Label"
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500"
            />
            {!readOnly && (
              <button type="button" onClick={() => remove(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!readOnly && values.length < 4 && (
        <button type="button" onClick={add} className="mt-1.5 flex items-center gap-1 text-xs text-brand-purple-600 hover:text-brand-purple-700 font-medium">
          <Plus className="w-3 h-3" /> Add stat
        </button>
      )}
    </div>
  );
}
